import dayjs from 'dayjs';
import _ from 'lodash';

import { EMPTY_GUID } from 'constants/data';
import { COMMON_DATE_FORMAT, COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { fixedTwoFractionDigitsExceptIntegerCurrencyOptions } from 'constants/loanApplication';
import { formatCalendarDate, formatDate } from 'utils/date';
import { formatNumberToCurrencyString } from 'utils/number';

export const getOptionNameByValue = (options, value) => {
  const option = options.find(opt => opt.value === value);

  return option?.name ?? '-';
};

export const getOptionNameOrValue = (options = [], value) => {
  if (_.isNil(value) || value === '') {
    return '-';
  }

  return options.find(option => option.value === value)?.name ?? value;
};

export const getOptionsWithCurrentValue = (options = [], value) =>
  _.isNil(value) || value === '' || options.some(option => option.value === value)
    ? options
    : [...options, { value, name: value }];

export const formatCurrency = value => {
  if (_.isNil(value)) {
    return '-';
  }

  return formatNumberToCurrencyString(value, fixedTwoFractionDigitsExceptIntegerCurrencyOptions);
};

export const formatDateValue = value => {
  if (!value) {
    return '-';
  }

  return formatCalendarDate(value, COMMON_DATE_FORMAT);
};

export const formatLocalDateValue = value =>
  value ? formatDate(value, COMMON_DATE_FORMAT, { includeLocalOffset: true }) : '-';

/**
 * Formats a date+user stamp into a human-readable string.
 * Returns "MM/DD/YYYY by Full Name" when both values are present,
 * just the date when the user name is absent, and "-" when the date is absent.
 *
 * @param {string|null} date - ISO date string
 * @param {string|null} userName - resolved display name of the acting user
 * @param {function(string): string} formatValue - formats the date portion
 * @returns {string}
 */
const formatStampedValue = (date, userName, formatValue) => {
  if (!date) {
    return '-';
  }

  const formattedDate = formatValue(date);

  return userName ? `${formattedDate} by ${userName}` : formattedDate;
};

const getStampedValueByUserFormatter = formatValue => (date, userId, getUserName) =>
  formatStampedValue(date, userId ? getUserName(userId) : null, formatValue);

export const formatStampedValueByUser = getStampedValueByUserFormatter(formatDateValue);

export const formatLocalStampedValueByUser = getStampedValueByUserFormatter(formatLocalDateValue);

/**
 * Formats a nullable boolean as "Yes", "No", or "-".
 *
 * @param {boolean|null} value
 * @returns {string}
 */
export const formatBoolValue = value => {
  if (_.isNil(value)) {
    return '-';
  }

  return value ? 'Yes' : 'No';
};

const formatRequestDate = date => (date ? dayjs(date).format(COMMON_REQUEST_DATE_FORMAT) : null);

const normalizeGuid = value => (value === EMPTY_GUID ? null : (value ?? null));

export const buildPostClosingPayload = ({
  loanId,
  postClosing,
  investorConditions,
  preInvestorConditions,
  closingDocumentRecords,
  tabId,
}) => {
  const investorId = normalizeGuid(postClosing?.investorId);

  return {
    loanId,
    shippingInfo: {
      collateralTrackingNumberToWarehouseBank:
        postClosing?.collateralTrackingNumberToWarehouseBank ?? null,
      collateralTrackingNumberToInvestor: postClosing?.collateralTrackingNumberToInvestor ?? null,
      noteTrackingNumberToInvestor: postClosing?.noteTrackingNumberToInvestor ?? null,
      noteTrackingNumberToWarehouse: postClosing?.noteTrackingNumberToWarehouse ?? null,
      collateralShippedToWarehouseDate: formatRequestDate(
        postClosing?.collateralShippedToWarehouseDate,
      ),
      collateralReceivedToWarehouseDate: formatRequestDate(
        postClosing?.collateralReceivedToWarehouseDate,
      ),
      collateralShippedToInvestorDate: formatRequestDate(
        postClosing?.collateralShippedToInvestorDate,
      ),
      collateralReceivedToInvestorDate: formatRequestDate(
        postClosing?.collateralReceivedToInvestorDate,
      ),
      noteShippedToWarehouseDate: formatRequestDate(postClosing?.noteShippedToWarehouseDate),
      noteReceivedToWarehouseDate: formatRequestDate(postClosing?.noteReceivedToWarehouseDate),
      noteShippedToInvestorDate: formatRequestDate(postClosing?.noteShippedToInvestorDate),
      noteReceivedToInvestorDate: formatRequestDate(postClosing?.noteReceivedToInvestorDate),
      trackingNumberTitleCompanyToLender: postClosing?.trackingNumberTitleCompanyToLender ?? null,
      shippedTitleCompanyToLenderDate: formatRequestDate(
        postClosing?.shippedTitleCompanyToLenderDate,
      ),
      receivedTitleCompanyToLenderDate: formatRequestDate(
        postClosing?.receivedTitleCompanyToLenderDate,
      ),
      originalMortgageTrackingNumberToInvestor:
        postClosing?.originalMortgageTrackingNumberToInvestor ?? null,
      noteTrackingNumberFromOriginalInvestor:
        postClosing?.noteTrackingNumberFromOriginalInvestor ?? null,
    },
    closingInvestor: investorId
      ? {
          investorId,
          commitmentNumber: postClosing?.commitmentNumber ?? null,
          conditions: postClosing?.conditions ?? null,
          dueDate: formatRequestDate(postClosing?.dueDate),
          investorLoanNumber: postClosing?.investorLoanNumber ?? null,
        }
      : null,
    investorConditions: (investorConditions ?? []).map(condition => ({
      id: condition.id,
      conditionText: condition.conditionText,
      dateAdded: formatRequestDate(condition.dateAdded),
      dateResponded: formatRequestDate(condition.dateResponded),
      dateCleared: formatRequestDate(condition.dateCleared),
    })),
    preInvestorConditions: (preInvestorConditions ?? []).map(condition => ({
      id: condition.id,
      conditionText: condition.conditionText,
      dateAdded: formatRequestDate(condition.dateAdded),
      dateResponded: formatRequestDate(condition.dateResponded),
      dateCleared: formatRequestDate(condition.dateCleared),
    })),
    accountingInformation: {
      discount: postClosing?.discount ?? null,
      escrowBalance: postClosing?.escrowBalance ?? null,
      firstDueToInvestor: formatRequestDate(postClosing?.firstDueToInvestor),
      firstPayment: formatRequestDate(postClosing?.firstPayment),
      fundingFee: postClosing?.fundingFee ?? null,
      interest: postClosing?.interest ?? null,
      principalBalance: postClosing?.principalBalance ?? null,
      principalPurchased: postClosing?.principalPurchased ?? null,
      taxFee: postClosing?.taxFee ?? null,
      investorWireAmount: postClosing?.investorWireAmount ?? null,
    },
    servicerInfo: {
      servicer: postClosing?.servicer ?? null,
      servicerLoanNumber: postClosing?.servicerLoanNumber ?? null,
      transmitDate: formatRequestDate(postClosing?.transmitDate),
      min: postClosing?.min ?? null,
      servicingOwnershipType: postClosing?.servicingOwnershipType ?? null,
      servicingStatus: postClosing?.servicingStatus ?? null,
      servicingStartDate: formatRequestDate(postClosing?.servicingStartDate),
      servicingEndDate: formatRequestDate(postClosing?.servicingEndDate),
      poolNumber: postClosing?.poolNumber ?? null,
      investorType: postClosing?.investorType ?? null,
    },
    paymentInformation: {
      autoDebitPayment: postClosing?.autoDebitPayment ?? null,
    },
    closingDocumentRecords,
    requestMetadata: { tabId },
  };
};
